'use client';

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { Results } from '@orama/orama';
import { orama, trimDescription } from '@/lib/orama';
import { createUrl } from '@/lib/utils';
import Link from 'next/link';

interface SearchPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SearchPopup({ isOpen, onClose }: SearchPopupProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<Results<any>>();
  const isSearchPage = usePathname() === '/search';
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Focus input when popup opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    setSearchValue(searchParams?.get('q') || '');
  }, [searchParams, setSearchValue]);

  useEffect(() => {
    if (isSearchPage && searchValue.trim()) {
      router.push(createUrl('/search', new URLSearchParams({ q: searchValue })));
    } else if (searchValue.trim()) {
      orama
        .search({
          term: searchValue,
          limit: 5
        })
        .then((results) => {
          if (results) setSearchResults(results);
        })
        .catch(console.log);
    }
  }, [searchValue, isSearchPage, router]);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const val = e.target as HTMLFormElement;
    const search = val.search as HTMLInputElement;
    const newParams = new URLSearchParams(searchParams.toString());

    if (search.value) {
      newParams.set('q', search.value);
    } else {
      newParams.delete('q');
    }

    router.push(createUrl('/search', newParams));
    onClose();
  }

  const showSearchResults = searchValue.length > 0 && !!searchResults && !isSearchPage;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 pt-16">
      <div
        ref={popupRef}
        className="w-full max-w-3xl rounded-lg border border-neutral-700 bg-[#1A1A1D] p-4 shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Search</h2>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-white hover:bg-neutral-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="relative w-full">
          <input
            ref={inputRef}
            type="text"
            name="search"
            placeholder="Search for products..."
            autoComplete="off"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="w-full rounded-lg border border-neutral-700 bg-[#1E1E1E] px-4 py-3 text-white placeholder:text-neutral-400"
          />
          <button
            type="submit"
            className="absolute right-0 top-0 mr-3 flex h-full items-center"
          >
            <MagnifyingGlassIcon className="h-5 w-5 text-white" />
          </button>
        </form>

        {showSearchResults && (
          <div className="mt-4 max-h-[60vh] overflow-y-auto">
            {searchResults.count ? (
              <ul className="space-y-2">
                {searchResults?.hits?.map((product) => (
                  <li
                    key={product.id}
                    className="break-words rounded-md text-gray-300"
                  >
                    <Link
                      href={`/product/${product.document.handle}`}
                      className="block scroll-m-12 px-4 py-3 rounded-md hover:bg-[#F1EFEC] hover:bg-opacity-20 hover:text-[#F1EFEC]"
                      onClick={onClose}
                    >
                      <div className="text-base font-semibold leading-5 truncate">
                        {product.document.title as string}
                      </div>
                      <div className="excerpt mt-1 text-sm leading-[1.35rem] text-gray-400 line-clamp-2">
                        {trimDescription((product.document.description || product.document.title) as string)}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-3 text-gray-300">
                <div className="text-base font-semibold leading-5">
                  No results found for &quot;{searchValue}&quot;
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
